'use client';

import { useEffect, useRef } from 'react';
import { Phone, PhoneOff, User } from 'lucide-react';
import { Drawer } from '@/shared/ui/Drawer';
import { useStaffCallStore } from '../hooks/useStaffCallClient';

interface IncomingCallPanelDesktopProps {
  onAccept: () => void;
  onDecline: () => void;
}

export function IncomingCallPanelDesktop({
  onAccept,
  onDecline,
}: IncomingCallPanelDesktopProps) {
  const store = useStaffCallStore();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isOpen = store.state === 'incoming' && !!store.incomingCall;

  useEffect(() => {
    // Captured once, and that matters: by the time cleanup runs React may have
    // already detached the element and nulled the ref, so reading
    // `audioRef.current` there can silently skip the pause — leaving the
    // ringtone playing after the call has been answered or declined.
    const audio = audioRef.current;
    if (isOpen && audio) {
      audio.play().catch(() => {});
    }
    return () => {
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
    };
  }, [isOpen]);

  return (
    <Drawer
      open={isOpen}
      onClose={onDecline}
      side="right"
      width="380px"
      title="Incoming Call"
      id="calls_incoming_panel"
      footer={
        <div className="flex gap-3 w-full">
          <button
            id="calls_incoming_decline_btn"
            type="button"
            onClick={onDecline}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-hms-red/10 text-hms-red hover:bg-hms-red/20 transition-colors text-sm font-medium"
          >
            <PhoneOff className="w-4 h-4" />
            Decline
          </button>
          <button
            id="calls_incoming_accept_btn"
            type="button"
            onClick={onAccept}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-hms-green text-white hover:bg-hms-green/90 transition-colors text-sm font-medium"
          >
            <Phone className="w-4 h-4" />
            Accept
          </button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-5 py-6">
        {/* Avatar */}
        <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-hms-card border-2 border-hms-border animate-pulse">
          <User className="w-8 h-8 text-hms-gold" />
          <span className="absolute inset-0 rounded-full border-2 border-hms-gold/30 animate-ping" />
        </div>

        {/* Caller info */}
        <div className="text-center">
          <p
            id="calls_incoming_guest_name"
            className="text-lg font-semibold text-hms-text"
          >
            {store.incomingCall?.guestName ?? 'Guest'}
          </p>
          <p className="text-sm text-hms-textSub mt-0.5">
            {store.incomingCall?.hotelName ?? 'Hotel'}
          </p>
          <p className="text-xs text-hms-textDim mt-1">
            Booking {store.incomingCall?.bookingId}
          </p>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-hms-green/10 text-hms-green text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-hms-green animate-pulse" />
          Ringing...
        </div>
      </div>

      <audio ref={audioRef} loop preload="auto" src="/sounds/incoming-call.mp3" />
    </Drawer>
  );
}
